import React, { useState, useEffect } from 'react';
import { Pipeline, PipelineStage, pipelineAPI } from '../../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  RectangleStackIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import PipelineModal from './PipelineModal';
import StageModal from './StageModal';
import { ClientPipelineBoard } from '../pipelines';
import { Card, CardBody, CardHeader, Button, Badge, Spinner, EmptyState, Tabs, PageHero } from '../ui';

type TabType = 'config' | 'clients';

const PipelineManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('config');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [editingStage, setEditingStage] = useState<PipelineStage | null>(null);

  useEffect(() => {
    loadPipelines();
  }, []);

  useEffect(() => {
    if (selectedPipeline) {
      loadStages(selectedPipeline.id);
    }
  }, [selectedPipeline]);

  const loadPipelines = async () => {
    try {
      setLoading(true);
      const data = await pipelineAPI.getAll();
      setPipelines(data);
      if (data.length > 0 && !selectedPipeline) {
        setSelectedPipeline(data[0]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pipelines');
    } finally {
      setLoading(false);
    }
  };

  const loadStages = async (pipelineId: number) => {
    try {
      const data = await pipelineAPI.getStages(pipelineId);
      setStages(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load stages');
    }
  };

  const handleDeletePipeline = async (pipeline: Pipeline) => {
    if (!window.confirm(`Are you sure you want to delete "${pipeline.name}"?`)) {
      return;
    }

    try {
      await pipelineAPI.delete(pipeline.id);
      await loadPipelines();
      if (selectedPipeline?.id === pipeline.id) {
        setSelectedPipeline(pipelines[0] || null);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete pipeline');
    }
  };

  const handleDeleteStage = async (stage: PipelineStage) => {
    if (!window.confirm(`Are you sure you want to delete "${stage.name}"?`)) {
      return;
    }

    try {
      await pipelineAPI.deleteStage(stage.id);
      if (selectedPipeline) {
        await loadStages(selectedPipeline.id);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete stage');
    }
  };

  const handleEditPipeline = (pipeline: Pipeline) => {
    setEditingPipeline(pipeline);
    setShowPipelineModal(true);
  };

  const handleEditStage = (stage: PipelineStage) => {
    setEditingStage(stage);
    setShowStageModal(true);
  };

  const handlePipelineModalClose = () => {
    setShowPipelineModal(false);
    setEditingPipeline(null);
    loadPipelines();
  };

  const handleStageModalClose = () => {
    setShowStageModal(false);
    setEditingStage(null);
    if (selectedPipeline) {
      loadStages(selectedPipeline.id);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'config', label: 'Pipeline Configuration', icon: <Cog6ToothIcon className="h-5 w-5" /> },
    { id: 'clients', label: 'Client Pipeline Board', icon: <UserGroupIcon className="h-5 w-5" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Pipeline Management"
        subtitle="Configure sales pipelines and stages for deal and client tracking"
        icon={<FunnelIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="purple"
      >
        <div className="flex flex-wrap items-center gap-3 mt-2">
          {activeTab === 'config' && (
            <Button
              variant="primary"
              onClick={() => setShowPipelineModal(true)}
              leftIcon={<PlusIcon className="h-5 w-5" />}
            >
              New Pipeline
            </Button>
          )}
          {/* Tabs */}
          <div className="w-full mt-4">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={(tabId) => setActiveTab(tabId as TabType)}
            />
          </div>
        </div>
      </PageHero>

      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4">
            <div className="flex items-center text-error-600">
              <XCircleIcon className="h-5 w-5 mr-3" />
              <span>{error}</span>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Client Pipeline Board Tab */}
      {activeTab === 'clients' && (
        <Card variant="glass" hoverable clickable>
          <CardBody className="p-6">
            <ClientPipelineBoard isAdminContext={true} />
          </CardBody>
        </Card>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pipelines List */}
          <div className="lg:col-span-1">
            <Card variant="glass" hoverable clickable>
              <CardHeader
                title="Pipelines"
                icon={<RectangleStackIcon className="h-5 w-5 text-primary-600" />}
                action={<Badge variant="primary">{pipelines.length}</Badge>}
              />
              <CardBody className="p-0">
                <ul className="divide-y divide-neutral-100">
                  {pipelines.map((pipeline) => (
                    <li
                      key={pipeline.id}
                      className={`px-6 py-4 cursor-pointer transition-all duration-200 ${selectedPipeline?.id === pipeline.id
                        ? 'bg-primary-50 border-l-4 border-primary-600'
                        : 'hover:bg-neutral-50'
                        }`}
                      onClick={() => setSelectedPipeline(pipeline)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className={`text-sm font-semibold truncate ${selectedPipeline?.id === pipeline.id ? 'text-primary-900' : 'text-neutral-900'
                              }`}>
                              {pipeline.name}
                            </p>
                            {pipeline.isDefault && (
                              <Badge variant="success" size="sm">Default</Badge>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-neutral-500">
                            <Badge variant="neutral" size="sm">{pipeline._count?.deals || 0} deals</Badge>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPipeline(pipeline);
                            }}
                            title="Edit Pipeline"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePipeline(pipeline);
                            }}
                            className="text-error-600 hover:text-error-700 hover:bg-error-50"
                            title="Delete Pipeline"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {pipelines.length === 0 && (
                  <EmptyState
                    title="No pipelines yet"
                    description="Create your first pipeline to get started"
                    icon={<RectangleStackIcon className="h-12 w-12" />}
                    action={
                      <Button
                        variant="primary"
                        onClick={() => setShowPipelineModal(true)}
                        leftIcon={<PlusIcon className="h-5 w-5" />}
                      >
                        Create Pipeline
                      </Button>
                    }
                  />
                )}
              </CardBody>
            </Card>
          </div>

          {/* Stages List */}
          <div className="lg:col-span-2">
            {selectedPipeline ? (
              <Card variant="glass" hoverable clickable>
                <CardHeader
                  title={selectedPipeline.name}
                  subtitle={selectedPipeline.description}
                  action={
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary">{stages.length} stages</Badge>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowStageModal(true)}
                        leftIcon={<PlusIcon className="h-4 w-4" />}
                      >
                        Add Stage
                      </Button>
                    </div>
                  }
                />
                <CardBody className="p-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Order
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Stage Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Probability
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Deals
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-100">
                        {stages.map((stage) => (
                          <tr key={stage.id} className="hover:bg-neutral-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                                {stage.order}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                {stage.color && (
                                  <div
                                    className="w-4 h-4 rounded-full mr-3 shadow-sm border-2 border-white"
                                    style={{ backgroundColor: stage.color }}
                                  />
                                )}
                                <div>
                                  <div className="text-sm font-semibold text-neutral-900">
                                    {stage.name}
                                  </div>
                                  {stage.description && (
                                    <div className="text-xs text-neutral-500 mt-1 max-w-xs">
                                      {stage.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <div className="flex-1 bg-neutral-200 rounded-full h-2 max-w-[100px]">
                                  <div
                                    className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${stage.defaultProbability}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium text-neutral-900 min-w-[45px]">
                                  {stage.defaultProbability}%
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge variant="neutral">{stage._count?.deals || 0}</Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {stage.isClosedWon && (
                                <Badge variant="success">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Won
                                </Badge>
                              )}
                              {stage.isClosedLost && (
                                <Badge variant="error">
                                  <XCircleIcon className="h-3 w-3 mr-1" />
                                  Lost
                                </Badge>
                              )}
                              {!stage.isClosedWon && !stage.isClosedLost && (
                                <Badge variant="primary">Active</Badge>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditStage(stage)}
                                  title="Edit Stage"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteStage(stage)}
                                  className="text-error-600 hover:text-error-700 hover:bg-error-50"
                                  title="Delete Stage"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {stages.length === 0 && (
                      <EmptyState
                        title="No stages yet"
                        description="Add your first stage to start organizing your deals"
                        icon={<RectangleStackIcon className="h-12 w-12" />}
                        action={
                          <Button
                            variant="primary"
                            onClick={() => setShowStageModal(true)}
                            leftIcon={<PlusIcon className="h-5 w-5" />}
                          >
                            Add First Stage
                          </Button>
                        }
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            ) : (
              <Card variant="glass" hoverable clickable>
                <CardBody className="p-16">
                  <EmptyState
                    title="Select a pipeline"
                    description="Choose a pipeline from the left to view and manage its stages"
                    icon={<RectangleStackIcon className="h-16 w-16" />}
                  />
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showPipelineModal && (
        <PipelineModal
          pipeline={editingPipeline}
          onClose={handlePipelineModalClose}
        />
      )}

      {showStageModal && selectedPipeline && (
        <StageModal
          pipelineId={selectedPipeline.id}
          stage={editingStage}
          onClose={handleStageModalClose}
        />
      )}
    </div>
  );
};

export default PipelineManagement;

